import {
  Award,
  Building2,
  Crown,
  Flag,
  Flame,
  Gamepad2,
  Landmark,
  Swords,
  Timer,
  Trophy,
  Truck,
  Zap,
  type LucideIcon,
} from 'lucide-react';

import { tr } from '@/lib/locale';

/**
 * Achievement label/icon resolution. The API returns each earned achievement's
 * own `label` (title) + `key` (namespace); this module only supplies fallbacks
 * for the compact icon strip (which gets bare keys) and picks the lucide icon
 * drawn inside the laurel wreath for each namespace.
 *
 * Two key families exist: the prototype's themed set (`city`, `tycoon`, …)
 * and the namespaces the live backend actually awards (`games_played`,
 * `games_won`, `competition`, per the achievements seeder). Keys without a
 * curated icon fall back to a generic award so nothing renders empty.
 */

/** Curated lucide icon per namespace. */
const ICONS: Record<string, LucideIcon> = {
  games_played: Gamepad2,
  games_won: Trophy,
  competition: Swords,
  city: Building2,
  tycoon: Crown,
  hauler: Truck,
  streak: Flame,
  founder: Flag,
  speed: Zap,
  marathon: Timer,
  monopolist: Landmark,
};

/** Laurel colour family per namespace; the badge component maps it to classes. */
export type AchievementTone = 'gold' | 'green' | 'silver' | 'orange';

const TONES: Record<string, AchievementTone> = {
  games_won: 'gold',
  games_played: 'green',
  competition: 'silver',
};

/** Laurel colour for a namespace; orange for anything uncurated. */
export function achievementTone(key: string): AchievementTone {
  return Object.hasOwn(TONES, key) ? TONES[key] : 'orange';
}

/**
 * Namespaces with a curated, translated label. Anything outside this list is
 * humanized from the key itself.
 */
const LABELLED = [
  'city',
  'tycoon',
  'hauler',
  'streak',
  'founder',
  'speed',
  'marathon',
  'monopolist',
  'games_played',
  'games_won',
  'competition',
] as const;

/** Whether a curated icon exists for this namespace (else the generic award). */
export function hasAchievementIcon(key: string): boolean {
  return Object.hasOwn(ICONS, key);
}

/** Lucide icon for a namespace; generic award when none is curated. */
export function achievementIcon(key: string): LucideIcon {
  return hasAchievementIcon(key) ? ICONS[key] : Award;
}

/**
 * Human label for a namespace: a curated name, else the key humanized
 * (`games_won` → "Games Won") rather than a raw uppercase key.
 */
export function achievementLabel(key: string): string {
  if ((LABELLED as readonly string[]).includes(key)) return tr(`common:achievements.${key}`);
  return key
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
